import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  TagIcon,
  CalendarIcon,
  UsersIcon,
  CurrencyDollarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { discountApi, discountHelpers } from '../utils/discountApi';
import { showSuccessNotification, showErrorNotification, showWarningNotification } from './NotificationManager';
import CreateEditDiscountModal from './CreateEditDiscountModal';
import UsageStatsModal from './UsageStatsModal';

const DiscountManagement = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    page: 1,
    limit: 10
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [pagination, setPagination] = useState({});
  const [usageStats, setUsageStats] = useState(null);

  useEffect(() => {
    loadDiscounts();
  }, [filters]);

  const loadDiscounts = async () => {
    setLoading(true);
    try {
      const response = await discountApi.getAllDiscounts(filters);
      if (response.success) {
        setDiscounts(response.data || []);
        setPagination(response.pagination || {});
      } else {
        showErrorNotification('Failed to load discounts');
      }
    } catch (error) {
      console.error('Error loading discounts:', error);
      showErrorNotification('Failed to load discounts');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDiscount = async (discountId) => {
    if (!window.confirm('Are you sure you want to delete this discount?')) return;

    try {
      const response = await discountApi.deleteDiscount(discountId);
      if (response.success) {
        showSuccessNotification('Discount deleted successfully');
        loadDiscounts();
      } else {
        showErrorNotification('Failed to delete discount');
      }
    } catch (error) {
      console.error('Error deleting discount:', error);
      showErrorNotification('Failed to delete discount');
    }
  };

  const handleViewUsage = async (discount) => {
    setSelectedDiscount(discount);
    setUsageStats(null);
    setShowUsageModal(true);

    try {
      const response = await discountApi.getDiscountUsage(discount._id);
      if (response.success) {
        setUsageStats(response.data);
      } else {
        showErrorNotification('Failed to load usage statistics');
      }
    } catch (error) {
      console.error('Error loading usage stats:', error);
      showErrorNotification('Failed to load usage statistics');
    }
  };

  const filteredDiscounts = discounts.filter(discount =>
    discount.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    discount.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (discount) => {
    const status = discountHelpers.getDiscountStatus(discount);
    const colorClass = discountHelpers.getStatusColor(status);
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colorClass}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-crust">Discount Management</h2>
          <p className="text-olive-light">Create and manage discount codes</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-pepperoni to-primary-800 text-white px-4 py-2 rounded-xl font-semibold hover:shadow-lg transition-all"
        >
          <PlusIcon className="w-5 h-5" />
          Create Discount
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-olive-light" />
            <input
              type="text"
              placeholder="Search discounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-3">
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
              className="px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value, page: 1 }))}
              className="px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
            >
              <option value="">All Types</option>
              <option value="percentage">Percentage</option>
              <option value="fixed_amount">Fixed Amount</option>
              <option value="free_delivery">Free Delivery</option>
            </select>
          </div>
        </div>
      </div>

      {/* Discounts Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pepperoni"></div>
            <span className="ml-3 text-olive-light">Loading discounts...</span>
          </div>
        ) : filteredDiscounts.length === 0 ? (
          <div className="text-center py-12">
            <TagIcon className="w-12 h-12 text-olive-light mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-crust mb-2">No discounts found</h3>
            <p className="text-olive-light">Create your first discount to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-olive-light uppercase tracking-wider">
                    Code & Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-olive-light uppercase tracking-wider">
                    Type & Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-olive-light uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-olive-light uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-olive-light uppercase tracking-wider">
                    Valid Until
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-olive-light uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {filteredDiscounts.map((discount) => (
                  <motion.tr
                    key={discount._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-secondary-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-semibold text-crust">{discount.code}</div>
                        <div className="text-sm text-olive-light">{discount.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-semibold text-crust">
                          {discountHelpers.formatDiscountType(discount.type)}
                        </div>
                        <div className="text-sm text-olive-light">
                          {discountHelpers.formatDiscountValue(discount)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(discount)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="text-crust font-semibold">
                          {discount.usageCount || 0}
                          {discount.usageLimit && ` / ${discount.usageLimit}`}
                        </div>
                        <div className="text-olive-light">uses</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-olive-light">
                        {new Date(discount.validUntil).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewUsage(discount)}
                          className="p-1 text-primary-600 hover:text-primary-800 transition-colors"
                          title="View Usage"
                        >
                          <ChartBarIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedDiscount(discount);
                            setShowEditModal(true);
                          }}
                          className="p-1 text-olive-light hover:text-crust transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteDiscount(discount._id)}
                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-3 border-t border-secondary-200 flex items-center justify-between">
            <div className="text-sm text-olive-light">
              Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
              {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
              {pagination.totalItems} results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.currentPage <= 1}
                className="px-3 py-1 text-sm border border-secondary-300 rounded hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.currentPage >= pagination.totalPages}
                className="px-3 py-1 text-sm border border-secondary-300 rounded hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {(showCreateModal || showEditModal) && (
          <CreateEditDiscountModal
            isOpen={showCreateModal || showEditModal}
            onClose={() => {
              setShowCreateModal(false);
              setShowEditModal(false);
              setSelectedDiscount(null);
            }}
            discount={selectedDiscount}
            onSuccess={() => {
              loadDiscounts();
              setShowCreateModal(false);
              setShowEditModal(false);
              setSelectedDiscount(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Usage Statistics Modal */}
      <AnimatePresence>
        {showUsageModal && (
          <UsageStatsModal
            isOpen={showUsageModal}
            onClose={() => {
              setShowUsageModal(false);
              setSelectedDiscount(null);
              setUsageStats(null);
            }}
            discount={selectedDiscount}
            usageStats={usageStats}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DiscountManagement;